import React, { FC, useMemo, Suspense } from 'react'
import { graphql, useFragment, useLazyLoadQuery } from 'react-relay'

import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useMatch,
  useParams,
  useNavigate,
} from 'react-router-dom'

import { lazy as loadable } from '@loadable/component'

import {
  Button,
  Card,
  Column,
  Grid,
  GuildCard,
  Icon,
  Link,
  GuildMetadata,
  Screen,
  Stack,
  Text,
  NavigationSideBar,
  NavigationSideBarItem,
  useBreakpoint,
  wrapSuspenseComponent,
  wrapSuspenseScreen,
} from '@components'

import { YStack } from 'tamagui'

import { Button as NewButton } from '@newComponents'

import {
  GuildNavigatorQuery,
  GuildNavigatorQueryResponse,
} from './__generated__/GuildNavigatorQuery.graphql'

const GuildNetworksScreen = wrapSuspenseScreen(
  loadable(() => import('@screens/GuildNetworksScreen/GuildNetworksScreen'))
)

const GuildEventsScreen = wrapSuspenseScreen(
  loadable(() => import('@screens/GuildEventsScreen/GuildEventsScreen'))
)

const GuildEventsUpcomingScreen = wrapSuspenseScreen(
  loadable(
    () => import('@screens/GuildEventsUpcomingScreen/GuildEventsUpcomingScreen')
  )
)

const GuildPresentationsScreen = wrapSuspenseScreen(
  loadable(
    () => import('@screens/GuildPresentationsScreen/GuildPresentationsScreen')
  )
)

const GuildMembersScreen = wrapSuspenseScreen(
  loadable(() => import('@screens/GuildMembersScreen/GuildMembersScreen'))
)

const GuildDonationScreen = wrapSuspenseScreen(
  loadable(() => import('@screens/GuildDonationScreen/GuildDonationScreen'))
)

const GuildDonationConfirmationScreen = wrapSuspenseScreen(
  loadable(
    () =>
      import(
        '@screens/GuildDonationConfirmationScreen/GuildDonationConfirmationScreen'
      )
  )
)

const GuildEditNavigator = wrapSuspenseScreen(
  loadable(() => import('../GuildEditNavigator'))
)

const GuildJoinConfirmation = wrapSuspenseComponent(
  loadable(() => import('@modals/GuildJoinConfirmation/GuildJoinConfirmation'))
)

export const GuildNavigator: FC = () => {
  const params = useParams()

  const data = useLazyLoadQuery<GuildNavigatorQuery>(
    graphql`
      query GuildNavigatorQuery($id: String!) {
        nodeBySlugId(id: $id) {
          __typename

          ... on Guild {
            id
            slugId
            type

            myMembership {
              id
            }

            minimumDonationAmount
            donationCurrency

            ...GuildCard_guild
            ...GuildMetadata_guild
            ...GuildJoinConfirmation_guild
            ...GuildJoinConfirmationContent_guild

            totalNetworks: rankedNetworks(membershipStatus: ACCEPTED) {
              totalCount
            }

            totalGuilds: rankedNetworkGuilds(membershipStatus: ACCEPTED) {
              totalCount
            }

            totalEvents: events {
              totalCount
            }

            totalPresentations: presentations {
              totalCount
            }
          }
        }
      }
    `,
    { id: params.actorId }
  )

  const initialRoute = useMemo(() => {
    if (!data?.nodeBySlugId || data.nodeBySlugId.__typename !== 'Guild') {
      return 'members'
    }

    if (
      data.nodeBySlugId.type === 'NETWORK' &&
      data.nodeBySlugId.totalGuilds.totalCount > 0
    ) {
      return 'network'
    }

    if (data.nodeBySlugId.totalEvents.totalCount > 0) {
      return 'events'
    }

    if (data.nodeBySlugId.totalPresentations.totalCount > 0) {
      return 'presentations'
    }

    return 'members'
  }, [
    data?.type,
    data?.nodeBySlugId?.totalNetworks?.totalCount,
    data?.nodeBySlugId?.totalGuilds?.totalCount,
    data?.nodeBySlugId?.totalEvents?.totalCount,
    data?.nodeBySlugId?.totalPresentations?.totalCount,
  ])

  const location = useLocation()

  const locationState = location.state as { backgroundLocation?: Location }

  if (data.nodeBySlugId?.__typename !== 'Guild') {
    // TODO: Display Not Found Page
    return null
  }

  return (
    <>
      <GuildMetadata guild={data.nodeBySlugId} />

      <Routes location={locationState?.backgroundLocation || location}>
        <Route path='settings/*' element={<GuildEditNavigator />} />

        <Route
          path='*'
          element={
            <GuildNavigatorSidebar key={params.actorId} data={data}>
              <Routes location={locationState?.backgroundLocation || location}>
                <Route
                  path='confirm'
                  element={<GuildJoinConfirmation guild={data.nodeBySlugId} />}
                />

                <Route path='network' element={<GuildNetworksScreen />} />

                <Route path='events/*' element={<GuildEventsScreen />} />

                <Route
                  path='presentations'
                  element={<GuildPresentationsScreen />}
                />
                <Route path='members' element={<GuildMembersScreen />} />
                <Route path='donate' element={<GuildDonationScreen />} />
                <Route
                  path='donate/confirm'
                  element={<GuildDonationConfirmationScreen />}
                />

                <Route
                  path='*'
                  element={<Navigate to={initialRoute} replace />}
                />
              </Routes>
            </GuildNavigatorSidebar>
          }
        />
      </Routes>
    </>
  )
}

const GuildNavigatorCardSection: FC<{
  data: GuildNavigatorQueryResponse
}> = ({ data }) => {
  const params = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  // useMatch matches against location data which encodes any unicode slugs
  const actorIdMatch = encodeURIComponent(params.actorId as string)
  const networkMatch = useMatch(`/${actorIdMatch}/network`)
  const eventsMatch = useMatch(`/${actorIdMatch}/events/*`)
  const presentationsMatch = useMatch(`/${actorIdMatch}/presentations`)
  const membersMatch = useMatch(`/${actorIdMatch}/members`)

  const selectedIndex = useMemo<number>(() => {
    if (data.nodeBySlugId?.__typename !== 'Guild') {
      return 0
    }

    const hasEvents = data.nodeBySlugId.totalEvents.totalCount > 0
    const hasPresentations = data.nodeBySlugId.totalPresentations.totalCount > 0

    if (
      data.nodeBySlugId.type === 'NETWORK' &&
      data.nodeBySlugId.totalGuilds.totalCount > 0
    ) {
      if (networkMatch) {
        return 0
      }

      if (eventsMatch) {
        return 1
      }

      if (presentationsMatch) {
        if (hasEvents) {
          return 2
        }

        return 1
      }

      if (membersMatch) {
        if (hasEvents && hasPresentations) {
          return 3
        }

        if (hasEvents || hasPresentations) {
          return 2
        }

        return 1
      }
    }

    if (eventsMatch) {
      return 0
    }

    if (presentationsMatch) {
      if (hasEvents) {
        return 1
      }

      return 0
    }

    if (membersMatch) {
      if (hasEvents && hasPresentations) {
        return 2
      }

      if (hasEvents || hasPresentations) {
        return 1
      }

      return 0
    }

    if (
      data.nodeBySlugId?.__typename === 'Guild' &&
      data.nodeBySlugId.type === 'GUILD' &&
      data.nodeBySlugId.totalNetworks.totalCount > 0 &&
      networkMatch
    ) {
      if (hasEvents && hasPresentations) {
        return 3
      }

      if (hasEvents || hasPresentations) {
        return 2
      }

      return 1
    }

    return 0
  }, [data, networkMatch, eventsMatch, presentationsMatch, membersMatch])

  if (data.nodeBySlugId?.__typename !== 'Guild') {
    return null
  }

  let currentIndex = 0

  return (
    <Stack gap={4}>
      <GuildCard
        guild={data.nodeBySlugId}
        showSettingsButton={!!data.nodeBySlugId.myMembership}
        primaryButton
      />

      {!!data.nodeBySlugId.donationCurrency &&
        !!data.nodeBySlugId.minimumDonationAmount && (
          <NewButton
            successFlash={false}
            errorFlash={false}
            secondary
            onPress={() => {
              const slugId =
                data.nodeBySlugId?.__typename === 'Guild' &&
                data.nodeBySlugId?.slugId

              if (slugId) {
                navigate(`/${slugId}/donate`, {
                  state: { backgroundLocation: location },
                })
              }
            }}
          >
            <NewButton.Text size='$4' paddingRight='$2'>
              💖
            </NewButton.Text>
            <NewButton.Text size='$4' secondary>
              Donate
            </NewButton.Text>
          </NewButton>
        )}

      <Card gap={2}>
        <NavigationSideBar selectedIndex={selectedIndex} gap={2}>
          {data.nodeBySlugId.type === 'NETWORK' &&
            data.nodeBySlugId.totalGuilds.totalCount > 0 && (
              <Link to={`/${params.actorId}/network`}>
                <NavigationSideBarItem index={currentIndex++}>
                  <Stack direction='horizontal' alignItems='center' gap={3}>
                    <Icon
                      name='organization'
                      key={networkMatch ? 'match' : 'no-match'}
                      variant={
                        networkMatch !== null ? 'subNavFocused' : 'subNav'
                      }
                    />

                    <Text
                      variant={
                        networkMatch !== null ? 'subNavFocused' : 'subNav'
                      }
                    >
                      Network
                    </Text>
                  </Stack>
                </NavigationSideBarItem>
              </Link>
            )}

          {data.nodeBySlugId.totalEvents.totalCount > 0 && (
            <Link to={`/${params.actorId}/events`}>
              <NavigationSideBarItem index={currentIndex++}>
                <Stack direction='horizontal' alignItems='center' gap={3}>
                  <Icon
                    name='calendar'
                    key={eventsMatch ? 'match' : 'no-match'}
                    variant={eventsMatch !== null ? 'subNavFocused' : 'subNav'}
                  />

                  <Text
                    variant={eventsMatch !== null ? 'subNavFocused' : 'subNav'}
                  >
                    Events
                  </Text>
                </Stack>
              </NavigationSideBarItem>
            </Link>
          )}

          {data.nodeBySlugId.totalPresentations.totalCount > 0 && (
            <Link to={`/${params.actorId}/presentations`}>
              <NavigationSideBarItem index={currentIndex++}>
                <Stack direction='horizontal' alignItems='center' gap={3}>
                  <Icon
                    name='projection-screen'
                    key={presentationsMatch ? 'match' : 'no-match'}
                    variant={
                      presentationsMatch !== null ? 'subNavFocused' : 'subNav'
                    }
                  />

                  <Text
                    variant={
                      presentationsMatch !== null ? 'subNavFocused' : 'subNav'
                    }
                  >
                    Presentations
                  </Text>
                </Stack>
              </NavigationSideBarItem>
            </Link>
          )}

          <Link to={`/${params.actorId}/members`}>
            <NavigationSideBarItem index={currentIndex++}>
              <Stack direction='horizontal' alignItems='center' gap={3}>
                <Icon
                  name='people-community'
                  key={membersMatch ? 'match' : 'no-match'}
                  variant={membersMatch !== null ? 'subNavFocused' : 'subNav'}
                />

                <Text
                  variant={membersMatch !== null ? 'subNavFocused' : 'subNav'}
                >
                  Members
                </Text>
              </Stack>
            </NavigationSideBarItem>
          </Link>

          {data.nodeBySlugId.type === 'GUILD' &&
          data.nodeBySlugId.totalNetworks.totalCount > 0 ? (
            <Link to={`/${params.actorId}/network`}>
              <NavigationSideBarItem index={currentIndex++}>
                <Stack direction='horizontal' alignItems='center' gap={3}>
                  <Icon
                    name='organization'
                    key={networkMatch ? 'match' : 'no-match'}
                    variant={networkMatch !== null ? 'subNavFocused' : 'subNav'}
                  />

                  <Text
                    variant={networkMatch !== null ? 'subNavFocused' : 'subNav'}
                  >
                    Network
                  </Text>
                </Stack>
              </NavigationSideBarItem>
            </Link>
          ) : null}
        </NavigationSideBar>
      </Card>
    </Stack>
  )
}

const GuildNavigatorSidebar: FC<{
  data: GuildNavigatorQueryResponse
}> = ({ data, children }) => {
  const breakpoint = useBreakpoint()

  if (data.nodeBySlugId?.__typename !== 'Guild') {
    return null
  }

  if (breakpoint === 0) {
    return (
      <Screen>
        <Stack gap={4} expand>
          <GuildNavigatorCardSection data={data} />

          {children}
        </Stack>
      </Screen>
    )
  }

  return (
    <Screen>
      <Grid>
        <Column span={[12, 5, 4, 3]}>
          <YStack gap='$4' style={{ position: 'sticky', top: 84 }}>
            <GuildNavigatorCardSection data={data} />
          </YStack>
        </Column>

        <Column span={[12, 7, 8, 9]}>{children}</Column>
      </Grid>
    </Screen>
  )
}

export default GuildNavigator
